import {
  applySquareEffects,
  applyTileEffects,
  getModifierMeta,
  getSquareModifiers,
  getTileModifierMeta,
  getTileModifiers,
} from '../boardConfig';
import { GAME_CONFIG, LETTER_POINTS } from '../config/gameConfig';
import { normalizeTileModifiers } from '../config/shopOptions';
import { applyLetterPhaseEffects, applyWordPhaseEffects } from './scoringEffects';

const toUpper = (value) => String(value || '').toUpperCase();

const getLetterBaseScore = (letter) => LETTER_POINTS[toUpper(letter)] || 0;

const toModifierSnapshot = (modifierIds = [], getMeta) =>
  modifierIds.map((modifierId) => {
    const meta = getMeta(modifierId);
    return {
      id: modifierId,
      label: meta.label,
      description: meta.description,
    };
  });

const computeLetterScore = ({
  letter,
  tileId,
  boardTiles,
  tileModifierOverrides,
  activeGameEffects,
  wordLength,
  boardTileCount = 0,
}) => {
  const squareModifiers = getSquareModifiers(boardTiles, tileId);

  const rawTileMods = tileModifierOverrides?.tileModifiers;
  const tileModifiersObj =
    rawTileMods && typeof rawTileMods === 'object' && !Array.isArray(rawTileMods)
      ? normalizeTileModifiers(rawTileMods)
      : getTileModifiers(letter, tileModifierOverrides?.letterOverrides || tileModifierOverrides);

  const squareState = applySquareEffects(squareModifiers, {
    letterMultiplier: 1,
    wordMultiplier: 1,
    letterBonus: 0,
    wordBonus: 0,
  });

  const extrasState = applyTileEffects(
    tileModifiersObj,
    {
      letterMultiplier: 1,
      wordMultiplier: 1,
      letterBonus: 0,
      wordBonus: 0,
    },
    { letter, tileId, wordLength, boardTileCount }
  );

  const letterMultiplier =
    (squareState.letterMultiplier ?? 1) * (extrasState.letterMultiplier ?? 1);
  const tileScoreBonus = tileModifiersObj.score ?? 0;
  const tileScoreMultiplier = tileModifiersObj.multiplier ?? 1;
  const wordMultiplier =
    (squareState.wordMultiplier ?? 1) * (extrasState.wordMultiplier ?? 1) * tileScoreMultiplier;
  const letterBonus =
    (squareState.letterBonus ?? 0) + (extrasState.letterBonus ?? 0) + tileScoreBonus;
  const wordBonus = (squareState.wordBonus ?? 0) + (extrasState.wordBonus ?? 0);

  const base = getLetterBaseScore(letter);
  const localLetterScore = Math.max(0, base * letterMultiplier + letterBonus);
  const letterPhaseEffects = applyLetterPhaseEffects({
    activeGameEffects,
    letterContext: {
      tileId,
      letter: toUpper(letter),
      base,
      localLetterScore,
    },
  });
  const totalLetterBonus = letterBonus + letterPhaseEffects.letterBonus;
  const letterScoreBeforeClamp = base * letterMultiplier + totalLetterBonus;
  const letterScore = Math.max(0, letterScoreBeforeClamp);

  const extrasSnapshot = toModifierSnapshot(tileModifiersObj.extras, getTileModifierMeta);
  const syntheticModifiers = [
    ...(tileScoreBonus > 0
      ? [{ id: 'score-bonus', label: `+${tileScoreBonus}`, description: 'Score bonus' }]
      : []),
    ...(tileScoreMultiplier > 1
      ? [{ id: 'tile-multiplier', label: `x${tileScoreMultiplier} word`, description: 'Word score multiplier' }]
      : []),
    ...extrasSnapshot,
  ];

  return {
    letterScore,
    wordMultiplier,
    wordBonus,
    detail: {
      tileId,
      letter: toUpper(letter),
      base,
      squareModifiers: toModifierSnapshot(squareModifiers, getModifierMeta),
      tileModifiers: syntheticModifiers,
      tileScoreBonus,
      tileScoreMultiplier,
      letterMultiplier,
      letterBonus: totalLetterBonus,
      localLetterBonus: letterBonus,
      activeEffectBonus: letterPhaseEffects.letterBonus,
      activeEffectDetails: letterPhaseEffects.details,
      letterScore,
    },
  };
};

export const computeWordScore = ({
  word,
  boardTiles,
  tileModifierOverrides,
  placedTilesById,
  activeGameEffects,
  boardTileCount = 0,
}) => {
  const wordLength = word.cells.length;

  const computed = word.cells.reduce(
    (acc, tileId) => {
      const placedTile = placedTilesById[tileId];
      if (!placedTile) {
        return acc;
      }

      const { letterScore, wordMultiplier, wordBonus, detail } = computeLetterScore({
        letter: placedTile.letter,
        tileId,
        boardTiles,
        activeGameEffects,
        wordLength,
        boardTileCount,
        tileModifierOverrides: {
          tileModifiers: placedTile.modifiers,
          letterOverrides: tileModifierOverrides,
        },
      });

      return {
        letterTotal: acc.letterTotal + letterScore,
        wordMultiplier: acc.wordMultiplier * wordMultiplier,
        wordBonus: acc.wordBonus + wordBonus,
        letters: [...acc.letters, detail],
      };
    },
    {
      letterTotal: 0,
      wordMultiplier: 1,
      wordBonus: 0,
      letters: [],
    }
  );

  const wordPhaseEffects = applyWordPhaseEffects({
    activeGameEffects,
    wordContext: {
      wordText: word.text,
      letters: computed.letters.map((item) => item.letter),
      letterTotal: computed.letterTotal,
    },
  });

  const totalWordMultiplier = computed.wordMultiplier * wordPhaseEffects.wordMultiplier;
  const totalWordBonus = computed.wordBonus + wordPhaseEffects.wordBonus;

  const preBonusWordScore = computed.letterTotal * totalWordMultiplier;
  const { minLength, bonusPerTile } = GAME_CONFIG.wordLengthBonus;
  const intrinsicLengthBonus = Math.max(0, wordLength - (minLength - 1)) * bonusPerTile;
  const finalWordScore = Math.ceil(Math.max(0, preBonusWordScore + totalWordBonus + intrinsicLengthBonus));

  return {
    score: finalWordScore,
    breakdown: {
      word: word.text,
      letterTotal: computed.letterTotal,
      wordMultiplier: totalWordMultiplier,
      wordBonus: totalWordBonus,
      intrinsicLengthBonus,
      preBonusWordScore,
      finalWordScore,
      letters: computed.letters,
      activeWordEffectDetails: wordPhaseEffects.details,
    },
  };
};

export const computeMoveScore = ({
  words,
  boardTiles,
  tileModifierOverrides,
  placedTilesById,
  activeGameEffects = [],
  boardTileCount = 0,
}) => {
  const scoredWords = words.map((word) => {
    const result = computeWordScore({
      word,
      boardTiles,
      tileModifierOverrides,
      placedTilesById,
      activeGameEffects,
      boardTileCount,
    });

    return {
      ...word,
      score: result.score,
      scoreBreakdown: result.breakdown,
    };
  });

  return {
    totalScore: Math.ceil(scoredWords.reduce((acc, word) => acc + word.score, 0)),
    scoredWords,
  };
};
